import { createFileRoute, Link, redirect, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

// Pathless layout that gates all child admin pages
export const Route = createFileRoute("/_admin")({
  beforeLoad: async ({ location }) => {
    // Wait for the browser session to hydrate
    if (typeof window !== "undefined") {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        throw redirect({ to: "/admin/login", search: { redirect: location.href } });
      }
    }
  },
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminLayout,
});

function AdminHeader({ onSignOut }: { onSignOut: () => void }) {
  const [open, setOpen] = useState(false);

  const navItems = [
    { to: "/admin/blog", label: "Blog Posts" },
    { to: "/analytics/dashboard", label: "Analytics" },
    { to: "/", label: "View site" },
  ];

  return (
    <header className="border-b border-border bg-card">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <Link to="/admin/blog" className="font-semibold text-foreground">Admin</Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              {item.label}
            </Link>
          ))}
          <Button variant="outline" size="sm" onClick={onSignOut}>Sign out</Button>
        </nav>

        {/* Mobile menu button */}
        <button
          type="button"
          className="md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="border-t border-border px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-3">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Button variant="outline" size="sm" onClick={onSignOut} className="w-full">
              Sign out
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}

function AdminLayout() {
  const navigate = useNavigate();
  const [state, setState] = useState<"checking" | "ok" | "not-admin">("checking");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user.id;

        if (!userId) {
          if (!cancelled) setState("not-admin");
          return;
        }

        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .eq("role", "admin")
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;
        setState(data ? "ok" : "not-admin");
      } catch {
        if (!cancelled) setState("not-admin");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/admin/login" });
  }

  if (state === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Checking access…</p>
      </div>
    );
  }

  if (state === "not-admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-foreground">Not authorized</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account doesn't have admin access. Ask the site owner to grant you the admin role.
          </p>
          <Button onClick={handleSignOut} className="mt-6">Sign out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader onSignOut={handleSignOut} />
      <Outlet />
    </div>
  );
}
